import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiPieChart, FiSettings } from 'react-icons/fi';
import { Card, CardHeader, CardBody, DisclosureCard, LoadingSkeleton, CardSkeleton, HelpIcon, useLanguage } from '../components';
import ProgressBar from '../components/ProgressBar';
import { downloadCsv } from '../utils/exportCsv';
import ChartsPanel from './ChartsPanel';
import AnalyticsPanel from './AnalyticsPanel';
import {
  toFiniteNumber,
  normalizePercentValue,
  clamp,
  getHoldingExpectedReturnPct,
  getHoldingVolatilityPct,
  buildCorrelationMatrix,
  buildHoldingsSignature,
} from './portfolioBuilderUtils';
import './PortfolioGenerator.css';

const PortfolioSummary = ({ holdingsCount, investmentAmount, totalWeight, tt }) => (
  <div className="portfolio-summary pb-summary-wrap">
    <div className="summary-item">
      <span>{tt('Holdings:')}</span>
      <strong>{holdingsCount}</strong>
    </div>
    <div className="summary-item">
      <span>{tt('Investment:')}</span>
      <strong>${investmentAmount.toLocaleString()}</strong>
    </div>
    <div className="summary-item">
      <span>{tt('Allocated:')}</span>
      <strong className={Math.abs(totalWeight - 100) > 0.1 ? 'negative' : 'positive'}>
        {totalWeight.toFixed(1)}%
      </strong>
      {Math.abs(totalWeight - 100) > 0.1 && (
        <span className="pb-warning-inline">{tt('Not 100%')}</span>
      )}
    </div>
  </div>
);

const MetricsPanel = ({ metrics, tt }) => (
  <div className="portfolio-metrics pb-metrics-wrap">
    <h3 className="pb-sub-title">{tt('Portfolio Metrics')}</h3>
    <div className="metrics-grid">
      {metrics.map((metric) => (
        <div key={metric.label} className="metric-item">
          <span className="metric-label">{metric.label} {metric.help && <HelpIcon text={metric.help} />}</span>
          <span className="metric-value">{metric.value}</span>
          {metric.source && <span className="metric-source-label">{metric.source}</span>}
        </div>
      ))}
    </div>
  </div>
);

const AlwaysVisibleSection = ({ title, subtitle, className = '', children }) => (
  <section className={`pb-section-card ${className}`.trim()}>
    <div className="pb-section-head">
      <h3 className="pb-sub-title">{title}</h3>
      {subtitle && <p className="pb-section-subtitle">{subtitle}</p>}
    </div>
    <div className="pb-section-body">{children}</div>
  </section>
);

const PERSONA_DESCRIPTIONS = {
  growth_seeker: 'Focus on high-growth companies with strong earnings momentum. Higher volatility accepted for greater upside potential. Expected returns: 10–15% annually.',
  eco_friendly: 'Prioritize companies with strong ESG (Environmental, Social, Governance) profiles and sustainable practices. Moderate risk with ethical alignment.',
  balanced: 'Medium risk tolerance. Equal emphasis on growth and stability. Expected returns: 6–9% annually.',
  income_focus: 'Targets high dividend yields with stable, mature companies. Lower volatility with steady income streams. Expected returns: 4–7% annually.',
  conservative: 'Low risk tolerance. Prioritizes capital preservation with stable, income-generating assets. Expected returns: 4–6% annually.',
  moderate_conservative: 'Below-average risk. Balances stability with modest growth. Expected returns: 5–7% annually.',
  moderate_aggressive: 'Above-average risk. Tilts toward growth with higher volatility tolerance. Expected returns: 8–12% annually.',
  aggressive: 'High risk tolerance. Maximizes growth potential, accepts significant drawdowns. Expected returns: 10–15% annually.',
};

// Max sector % defaults per persona (higher = more concentrated OK)
const PERSONA_SECTOR_MAX = {
  growth_seeker: 50,
  aggressive: 80,
  moderate_aggressive: 50,
  eco_friendly: 40,
  income_focus: 35,
  balanced: 50,
  moderate_conservative: 25,
  conservative: 20,
};

const PERSONA_ASSET_DEFAULTS = {
  growth_seeker: { include_etfs: true, include_bonds: false },
  aggressive: { include_etfs: true, include_bonds: false },
  moderate_aggressive: { include_etfs: true, include_bonds: false },
  eco_friendly: { include_etfs: true, include_bonds: true },
  income_focus: { include_etfs: true, include_bonds: true },
  balanced: { include_etfs: true, include_bonds: true },
  moderate_conservative: { include_etfs: true, include_bonds: true },
  conservative: { include_etfs: true, include_bonds: true },
};

const PortfolioGenerator = ({ apiBase }) => {
  const { tt } = useLanguage();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [formData, setFormData] = useState({
    persona_name: 'balanced',
    investment_amount: 100000,
    min_holdings: 10,
    max_holdings: 40,
    max_position_pct: 25,
    max_sector_pct: 50,
    include_bonds: false,
    include_etfs: false,
    rebalance_threshold: 5
  });
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [personasLoading, setPersonasLoading] = useState(true);
  const [backendFrontier, setBackendFrontier] = useState(null);
  const [frontierFallbackReason, setFrontierFallbackReason] = useState('insufficient assets');
  const [historicalPerformance, setHistoricalPerformance] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState('frontier');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [backtestData, setBacktestData] = useState(null);
  const [stressData, setStressData] = useState(null);
  const [riskDecompData, setRiskDecompData] = useState(null);
  const [driftData, setDriftData] = useState(null);
  const [brinsonData, setBrinsonData] = useState(null);
  const [costBps, setCostBps] = useState(5);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('1Y');
  const [analyticsBenchmarkTicker, setAnalyticsBenchmarkTicker] = useState('SPY');
  const [analyticsRebalanceFrequency, setAnalyticsRebalanceFrequency] = useState('monthly');
  const [driftThresholdPct, setDriftThresholdPct] = useState(5);

  const toDecimal = (value) => {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return null;
    return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
  };

  const formatPercent = (value, digits = 2) => {
    const decimalValue = toDecimal(value);
    if (decimalValue === null) return tt('N/A');
    return `${(decimalValue * 100).toFixed(digits)}%`;
  };

  const buildDisplayMetrics = () => {
    if (!portfolio) return [];

    const summary = portfolio.summary || {};
    const metrics = portfolio.metrics || {};

    const expectedReturn =
      toDecimal(summary.expected_return) ??
      toDecimal(metrics.expected_return);

    const volatility =
      toDecimal(summary.volatility) ??
      toDecimal(metrics.volatility);

    const sharpeRatio =
      toFiniteNumber(metrics.sharpe_ratio) ??
      (expectedReturn !== null && volatility && volatility > 0
        ? (expectedReturn - 0.02) / volatility
        : null);

    const fallbackHhi = holdings.length
      ? holdings.reduce((sum, holding) => {
          const normalizedWeight = Number(holding.weight || 0) / 100;
          return sum + (normalizedWeight * normalizedWeight);
        }, 0)
      : null;

    const herfindahl = toFiniteNumber(metrics.herfindahl_index) ?? fallbackHhi;
    const largestPosition = holdings.length
      ? Math.max(...holdings.map((holding) => Number(holding.weight || 0)))
      : null;
    const diversificationScore = herfindahl !== null
      ? Math.max(0, Math.min(100, (1 - herfindahl) * 100))
      : null;

    return [
      {
        label: tt('Expected Return'),
        value: formatPercent(expectedReturn),
        help: tt('Annualized return estimate from a Fama-French 5-factor model. Reflects forward-looking factor exposures, not past performance.'),
        source: tt('FF5 factor model'),
      },
      {
        label: tt('Volatility'),
        value: formatPercent(volatility),
        help: tt('Annualized standard deviation of returns. Higher volatility means larger price swings and greater uncertainty.'),
      },
      {
        label: tt('Sharpe Ratio'),
        value: sharpeRatio !== null ? sharpeRatio.toFixed(2) : tt('N/A'),
        help: tt('Risk-adjusted return: (Expected Return − Risk-Free Rate) ÷ Volatility. Values above 1.0 are good; above 2.0 is excellent.'),
      },
      {
        label: tt('Number of Holdings'),
        value: String(holdings.length || 0),
        help: tt('Total active holdings in the generated portfolio.'),
      },
      {
        label: tt('Largest Position'),
        value: Number.isFinite(largestPosition) ? `${largestPosition.toFixed(2)}%` : tt('N/A'),
        help: tt('Highest single-holding weight in the generated portfolio.'),
      },
      {
        label: tt('Diversification Score'),
        value: diversificationScore !== null ? `${diversificationScore.toFixed(0)}/100` : tt('N/A'),
        help: tt('Diversification score derived from concentration. Higher values indicate broader diversification.'),
        source: herfindahl !== null ? tt('HHI derived') : null,
      }
    ];
  };

  useEffect(() => {
    const fetchPersonas = async () => {
      setPersonasLoading(true);
      try {
        const response = await axios.get(`${apiBase}/personas`);
        setPersonas(response.data);
      } catch (error) {
        console.error('Error fetching personas:', error);
        setError(tt('Unable to load investment profiles. Please refresh the page.'));
      } finally {
        setPersonasLoading(false);
      }
    };

    fetchPersonas();
  }, [apiBase, tt]);

  // Auto-update constraints and default ETF/bond preferences when persona changes
  useEffect(() => {
    const sectorMax = PERSONA_SECTOR_MAX[formData.persona_name];
    const assetDefaults = PERSONA_ASSET_DEFAULTS[formData.persona_name];

    setFormData(prev => ({
      ...prev,
      max_sector_pct: sectorMax !== undefined ? sectorMax : prev.max_sector_pct,
      include_etfs: assetDefaults?.include_etfs ?? prev.include_etfs,
      include_bonds: assetDefaults?.include_bonds ?? prev.include_bonds,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.persona_name]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked :
              name === 'investment_amount' ? parseFloat(value) :
              name.includes('_') && name !== 'persona_name' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${apiBase}/portfolio/generate`, formData);
      setPortfolio(response.data);
    } catch (error) {
      console.error('Error generating portfolio:', error);
      setError(error.response?.data?.message || error.response?.data?.error || error.response?.data?.detail || tt('Unable to generate your portfolio. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const holdings = useMemo(
    () => (portfolio?.holdings || []).filter((holding) => Number(holding?.weight) > 0 && !!holding?.ticker),
    [portfolio]
  );
  const activeHoldingsSignature = buildHoldingsSignature(holdings);

  useEffect(() => {
    if (!holdings.length) {
      setBackendFrontier(null);
      setFrontierFallbackReason('insufficient assets');
      setHistoricalPerformance(null);
      return;
    }

    let cancelled = false;

    const fetchHistorical = async () => {
      try {
        const payload = {
          holdings: holdings.map((holding) => ({ ticker: holding.ticker, weight: holding.weight })),
          period: analyticsPeriod,
          initial_value: portfolio?.investment_amount || formData.investment_amount
        };
        const response = await axios.post(`${apiBase}/portfolio/historical-performance`, payload);
        const series = Array.isArray(response?.data?.series) ? response.data.series : [];
        if (!cancelled && series.length >= 2) {
          const maxPoints = 60;
          let thinned = series;
          if (series.length > maxPoints) {
            const step = Math.ceil(series.length / maxPoints);
            thinned = series.filter((_, idx) => idx % step === 0 || idx === series.length - 1);
          }
          setHistoricalPerformance(
            thinned.map((point) => ({
              date: point.date?.length > 7 ? point.date.slice(5) : point.date,
              value: point.value
            }))
          );
        }
      } catch (_err) {
        if (!cancelled) {
          setHistoricalPerformance(null);
        }
      }
    };

    const fetchFrontier = async () => {
      if (holdings.length < 2) {
        setBackendFrontier(null);
        setFrontierFallbackReason('insufficient assets');
        return;
      }
      try {
        const payload = {
          holdings: holdings.map((holding) => ({ ticker: holding.ticker, weight: holding.weight }))
        };
        const response = await axios.post(`${apiBase}/portfolio/efficient-frontier`, payload);
        const frontier = Array.isArray(response?.data?.frontier)
          ? response.data.frontier.filter((point) => Number.isFinite(point?.risk) && Number.isFinite(point?.return))
          : [];
        const assetPoints = Array.isArray(response?.data?.asset_points)
          ? response.data.asset_points.filter((point) => Number.isFinite(point?.risk) && Number.isFinite(point?.return))
          : [];

        if (!cancelled && frontier.length > 0) {
          setBackendFrontier({ signature: activeHoldingsSignature, frontier, assetPoints });
          setFrontierFallbackReason(null);
        } else if (!cancelled) {
          setBackendFrontier(null);
          setFrontierFallbackReason('insufficient frontier data');
        }
      } catch (_err) {
        if (!cancelled) {
          setBackendFrontier(null);
          setFrontierFallbackReason('backend unavailable');
        }
      }
    };

    fetchHistorical();
    fetchFrontier();

    return () => {
      cancelled = true;
    };
  }, [activeHoldingsSignature, analyticsPeriod, apiBase, formData.investment_amount, holdings, portfolio]);

  const runAnalytics = async (tab) => {
    if (!holdings.length) return;
    setAnalyticsLoading(true);
    try {
      const benchmarkTicker = String(analyticsBenchmarkTicker || 'SPY')
        .toUpperCase()
        .replace(/[^A-Z0-9.-]/g, '')
        .slice(0, 10) || 'SPY';
      const benchmarkPeriod = ['1M', '3M', '6M', '1Y'].includes(analyticsPeriod) ? analyticsPeriod : '1Y';
      const attributionPeriod = ['1M', '3M', '6M', '1Y', '2Y'].includes(analyticsPeriod) ? analyticsPeriod : '1Y';

      const holdingsPayload = holdings.map((holding) => ({
        ticker: holding.ticker,
        weight: holding.weight / 100,
      }));

      if (tab === 'benchmark') {
        const res = await axios.post(`${apiBase}/portfolio/benchmark-analytics`, {
          holdings: holdingsPayload,
          benchmark_ticker: benchmarkTicker,
          period: benchmarkPeriod,
        });
        setBenchmarkData(res?.data || null);
      } else if (tab === 'backtest') {
        const res = await axios.post(`${apiBase}/portfolio/backtest`, {
          holdings: holdingsPayload,
          initial_value: Number(portfolio?.investment_amount || formData.investment_amount || 100000),
          period: benchmarkPeriod,
          rebalance_frequency: analyticsRebalanceFrequency,
          transaction_cost_bps: Number(costBps) || 0,
        });
        setBacktestData(res?.data || null);
      } else if (tab === 'stress') {
        const res = await axios.post(`${apiBase}/portfolio/stress-test`, { holdings: holdingsPayload });
        setStressData(res?.data || null);
      } else if (tab === 'riskDecomp') {
        const res = await axios.post(`${apiBase}/portfolio/risk-decomposition`, { holdings: holdingsPayload });
        setRiskDecompData(res?.data || null);
      } else if (tab === 'drift') {
        const res = await axios.post(`${apiBase}/portfolio/drift-monitor`, {
          holdings: holdingsPayload,
          rebalance_threshold_pct: Number(driftThresholdPct) || 5,
        });
        setDriftData(res?.data || null);
      } else if (tab === 'brinson') {
        const res = await axios.post(`${apiBase}/portfolio/brinson-attribution`, {
          holdings: holdingsPayload,
          benchmark_ticker: benchmarkTicker,
          period: attributionPeriod,
        });
        setBrinsonData(res?.data || null);
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail;
      setError(apiMessage || tt('Advanced analytics are temporarily unavailable. Please try again.'));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const getAssetTypeDistribution = () => {
    if (!holdings.length) return [];

    const stocks = holdings.filter((holding) => (holding.type || 'stock') === 'stock');
    const etfs = holdings.filter((holding) => holding.type === 'etf');
    const bonds = holdings.filter((holding) => holding.type === 'bond');

    const data = [];
    if (stocks.length > 0) data.push({ name: tt('Stocks'), value: Number(stocks.reduce((sum, holding) => sum + (holding.weight || 0), 0).toFixed(2)) });
    if (etfs.length > 0) data.push({ name: tt('ETFs'), value: Number(etfs.reduce((sum, holding) => sum + (holding.weight || 0), 0).toFixed(2)) });
    if (bonds.length > 0) data.push({ name: tt('Bonds'), value: Number(bonds.reduce((sum, holding) => sum + (holding.weight || 0), 0).toFixed(2)) });
    
    return data;
  };

  const getSectorDistribution = () => {
    if (!holdings.length) return [];

    const sectorMap = {};
    holdings.forEach((holding) => {
      const sector = holding.sector || tt('Unknown');
      sectorMap[sector] = (sectorMap[sector] || 0) + holding.weight;
    });

    const entries = Object.entries(sectorMap).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2))
    }));
    return entries.sort((left, right) => right.value - left.value);
  };

  const getTopHoldingsAllocation = () => {
    const sorted = [...holdings]
      .sort((left, right) => (right.weight || 0) - (left.weight || 0));

    const top = sorted.slice(0, 5).map((holding) => ({
      name: holding.ticker,
      value: Number((holding.weight || 0).toFixed(2)),
    }));

    const otherWeight = sorted.slice(5).reduce((sum, holding) => sum + (holding.weight || 0), 0);
    if (otherWeight > 0) {
      top.push({ name: tt('Other'), value: Number(otherWeight.toFixed(2)) });
    }
    return top;
  };

  const getPerformanceSeries = () => {
    const summary = portfolio?.summary || {};
    const metrics = portfolio?.metrics || {};
    const expectedReturn = normalizePercentValue(summary.expected_return ?? metrics.expected_return);
    if (!Number.isFinite(expectedReturn) || expectedReturn <= -95) return [];

    const annualReturn = clamp(expectedReturn, -80, 120) / 100;
    const monthlyRate = Math.pow(1 + annualReturn, 1 / 12) - 1;
    let value = Number.isFinite(portfolio?.investment_amount) && portfolio?.investment_amount > 0
      ? portfolio.investment_amount
      : formData.investment_amount;

    return Array.from({ length: 12 }, (_, idx) => {
      value *= 1 + monthlyRate;
      return { date: `M${idx + 1}`, value: Number(value.toFixed(2)) };
    });
  };

  const getRiskReturnData = () => {
    if (backendFrontier && backendFrontier.signature === activeHoldingsSignature && backendFrontier.assetPoints.length > 0) {
      return backendFrontier.assetPoints;
    }

    return holdings
      .map((holding) => ({
        name: holding.ticker,
        return: getHoldingExpectedReturnPct(holding),
        risk: getHoldingVolatilityPct(holding),
        weight: holding.weight,
      }))
      .filter((point) => Number.isFinite(point.return) && Number.isFinite(point.risk));
  };

  const getCorrelationData = () => {
    const labels = holdings.map((holding) => holding.ticker);
    if (labels.length < 2) return { labels, matrix: [] };
    return { labels, matrix: buildCorrelationMatrix(holdings) };
  };

  const getEfficientFrontierData = () => {
    if (backendFrontier && backendFrontier.signature === activeHoldingsSignature && backendFrontier.frontier.length > 1) {
      return backendFrontier.frontier;
    }
    return [];
  };

  const openTickerInResearch = (ticker) => {
    const normalizedTicker = String(ticker || '').trim().toUpperCase();
    if (!normalizedTicker) return;
    navigate(`/market-data?ticker=${encodeURIComponent(normalizedTicker)}`);
  };

  const handleSavePortfolio = async () => {
    const authUser = localStorage.getItem('authUser');
    if (!authUser) {
      setSaveMessage(tt('Please sign in to save your portfolio.'));
      return;
    }

    if (!portfolio) {
      setSaveMessage(tt('Generate a portfolio before saving.'));
      return;
    }

    const name = saveName.trim() || `${portfolio.persona || tt('Optimized')} ${tt('Portfolio')}`;
    const payload = {
      name,
      source: 'optimizer',
      data: {
        ...portfolio
      }
    };

    setSaving(true);
    setSaveMessage(null);
    try {
      await axios.post(`${apiBase}/portfolios/save`, payload, { withCredentials: true });
      setSaveMessage(tt('Portfolio saved.'));
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.data?.detail;
      setSaveMessage(apiMessage || tt('Unable to save your portfolio. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = () => {
    if (!portfolio?.holdings?.length) return;
    const headers = ['Ticker', 'Name', 'Type', 'Market', 'Sector', 'Shares', 'Value', 'Weight (%)'];
    const rows = portfolio.holdings.map((h) => [
      h.ticker, h.name || '', h.type || 'stock', h.exchange || '', h.sector || '',
      h.shares?.toFixed(2) || '', h.value?.toFixed(2) || '', h.weight?.toFixed(2) || ''
    ]);
    const name = saveName.trim() || portfolio.persona || 'portfolio';
    downloadCsv(`${name}_holdings`, headers, rows);
  };

  const openInBuilder = () => {
    if (!portfolio) return;
    const draftName = saveName.trim() || `${portfolio.persona || tt('Optimized')} ${tt('Portfolio')}`;
    localStorage.setItem('builderDraftPortfolio', JSON.stringify({
      name: draftName,
      investment_amount: portfolio.investment_amount,
      holdings: portfolio.holdings,
      summary: portfolio.summary || null,
      metrics: portfolio.metrics || null
    }));
    navigate('/portfolio-builder');
  };

  const displayMetrics = buildDisplayMetrics();
  const assetTypeDistribution = holdings.length ? getAssetTypeDistribution() : [];
  const sectorDistribution = portfolio ? getSectorDistribution() : [];
  const topHoldingsDistribution = portfolio ? getTopHoldingsAllocation() : [];
  const performanceSeries = portfolio ? getPerformanceSeries() : [];
  const riskReturnData = holdings.length ? getRiskReturnData() : [];
  const totalWeight = holdings.reduce((sum, holding) => sum + (holding.weight || 0), 0);
  const allocationGap = Math.abs(totalWeight - 100);
  const isBackendFrontierActive =
    backendFrontier &&
    backendFrontier.signature === activeHoldingsSignature &&
    backendFrontier.frontier.length > 1;
  const frontierModelLabel = isBackendFrontierActive
    ? tt('Advanced covariance model')
    : tt('Estimated model');
  const investmentAmount = Number(portfolio?.investment_amount || formData.investment_amount || 0);

  return (
    <div className="page-container portfolio-generator-root portfolio-builder-root">
      <div className="page-header">
        <h1 className="pg-page-title">{tt('Generate a portfolio idea')}</h1>
        <p className="page-subtitle pg-page-subtitle">{tt('Answer a few portfolio preferences, then inspect the advanced optimization output only if it helps your decision.')}</p>
      </div>

      <div className={`pb-layout-grid ${portfolio ? 'with-results' : 'single-column'}`}>
        <div>
          <form className="form-section pb-form-section" onSubmit={handleSubmit}>
            <div className="pb-step-head">
              <span className="pb-step-index">1</span>
              <h2 className="pb-section-title">{tt('Start with your investing profile')}</h2>
            </div>

            <DisclosureCard
              title={tt('Profile and budget')}
              subtitle={tt('Choose your portfolio persona and investment amount.')}
              alwaysOpen
              className="pg-form-disclosure"
            >
              <div className="form-group">
                <label className="pg-label-row">
                  <FiSettings size={16} />
                  {tt('Portfolio Persona')}
                </label>
                {personasLoading ? (
                  <LoadingSkeleton height="44px" borderRadius="8px" />
                ) : (
                  <>
                  <select
                    name="persona_name"
                    value={formData.persona_name}
                    onChange={handleInputChange}
                    className="form-control pg-input-lg"
                  >
                    {personas.map(p => (
                      <option key={p.name} value={p.name}>
                        {p.display_name} {p.risk_tolerance && `(${p.risk_tolerance})`}
                      </option>
                    ))}
                  </select>
                  {PERSONA_DESCRIPTIONS[formData.persona_name] && (
                    <p className="pg-persona-desc">{tt(PERSONA_DESCRIPTIONS[formData.persona_name])}</p>
                  )}
                  <p className="pg-auto-selection-note">{tt('ETF and bond options are preselected for this profile, but you can still change them below.')}</p>
                  </>
                )}
              </div>

              <div className="form-group pg-compact-group">
                <label className="pg-label-row">
                  <FiDollarSign size={16} />
                  {tt('Investment Amount')}
                </label>
                <input
                  type="number"
                  name="investment_amount"
                  value={formData.investment_amount}
                  onChange={handleInputChange}
                  min="1000"
                  step="1000"
                  className="form-control pg-input-lg"
                  placeholder={tt('e.g., 100000')}
                />
              </div>
            </DisclosureCard>

            <DisclosureCard
              title={tt('Construction limits')}
              subtitle={tt('Adjust diversification and concentration constraints.')}
              alwaysOpen
              className="pg-form-disclosure"
            >
              <div className="pg-two-col-grid">
                <div className="form-group">
                  <label>{tt('Min Holdings')}</label>
                  <input
                    type="number"
                    name="min_holdings"
                    value={formData.min_holdings}
                    onChange={handleInputChange}
                    min="5"
                    max="50"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{tt('Max Holdings')}</label>
                  <input
                    type="number"
                    name="max_holdings"
                    value={formData.max_holdings}
                    onChange={handleInputChange}
                    min="5"
                    max="50"
                    className="form-control"
                  />
                </div>
              </div>

              <div className="pg-two-col-grid">
                <div className="form-group">
                  <label>{tt('Max Position (%)')} <HelpIcon text={tt('Maximum percentage of the portfolio that any single holding can represent. Lower values increase diversification.')} /></label>
                  <input
                    type="number"
                    name="max_position_pct"
                    value={formData.max_position_pct}
                    onChange={handleInputChange}
                    min="1"
                    max="100"
                    step="0.5"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>{tt('Max Sector (%)')} <HelpIcon text={tt('Maximum percentage of the portfolio allocated to any single sector (e.g., Technology, Healthcare). Helps avoid concentration risk.')} /></label>
                  <input
                    type="number"
                    name="max_sector_pct"
                    value={formData.max_sector_pct}
                    onChange={handleInputChange}
                    min="1"
                    max="100"
                    step="0.5"
                    className="form-control"
                  />
                </div>
              </div>
            </DisclosureCard>

            <DisclosureCard
              title={tt('Asset-type preferences')}
              subtitle={tt('Choose whether ETFs and bonds are included.')}
              alwaysOpen
              className="pg-form-disclosure"
            >
              <div className="form-group pg-compact-group">
                <label className={`pg-check-row ${formData.include_etfs ? 'is-checked' : ''}`}>
                  <input
                    type="checkbox"
                    name="include_etfs"
                    checked={formData.include_etfs}
                    onChange={handleInputChange}
                    className="form-check-input pg-check-input"
                  />
                  <span>{tt('Include ETFs')}</span>
                </label>
              </div>

              <div className="form-group pg-loose-group">
                <label className={`pg-check-row ${formData.include_bonds ? 'is-checked' : ''}`}>
                  <input
                    type="checkbox"
                    name="include_bonds"
                    checked={formData.include_bonds}
                    onChange={handleInputChange}
                    className="form-check-input pg-check-input"
                  />
                  <span>{tt('Include Bonds')}</span>
                </label>
              </div>
            </DisclosureCard>

            {error && (
              <div className="error-message pg-form-error">
                {error}
                <button type="button" className="pg-retry-btn" onClick={() => { setError(null); }}>{tt('Dismiss')}</button>
              </div>
            )}

            <button type="submit" className="pb-action-btn pb-save-btn pg-generate-btn" disabled={loading}>
              {loading ? tt('Generating...') : tt('Generate portfolio idea')}
            </button>
          </form>
        </div>

        {loading && (
          <div>
            <Card>
              <CardHeader>
                <div className="pg-loading-head">
                  <FiPieChart size={20} className="pg-icon-cyan" />
                  <h2 className="pg-card-title">{tt('Generating Portfolio...')}</h2>
                </div>
              </CardHeader>
              <CardBody>
                <ProgressBar active={loading} estimatedMs={6000} label={tt('Generating portfolio...')} />
                <div className="pg-loading-grid">
                  <CardSkeleton />
                  <LoadingSkeleton height="300px" borderRadius="12px" />
                  <LoadingSkeleton height="200px" borderRadius="12px" />
                </div>
              </CardBody>
            </Card>
          </div>
        )}

        {portfolio && !loading && (
          <div>
            <div className="results-section">
              <div className={`pb-health-bar ${allocationGap > 0.1 ? 'warning' : 'healthy'}`}>
                <div className="pb-health-items">
                  <span><strong>{holdings.length}</strong> {tt('holdings')}</span>
                  <span><strong>${investmentAmount.toLocaleString()}</strong> {tt('investment')}</span>
                  <span>{tt('Allocation')} <strong>{totalWeight.toFixed(1)}%</strong></span>
                </div>
                {allocationGap > 0.1 ? (
                  <span className="pb-health-ok pg-health-attn">{tt('Review allocation')}</span>
                ) : (
                  <span className="pb-health-ok">{tt('Balanced')}</span>
                )}
              </div>

              <div className="pb-results-head">
                <div className="pb-step-head pb-inline-head">
                  <span className="pb-step-index">2</span>
                  <h2 className="pb-section-title">{tt('Review & Save')}</h2>
                  <span className={`pb-model-badge ${isBackendFrontierActive ? 'backend' : 'fallback'}`}>
                    {frontierModelLabel}
                  </span>
                </div>
                <div className="pb-results-actions">
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder={tt('Portfolio name')}
                    className="form-control pb-name-input"
                  />
                  <button
                    type="button"
                    onClick={handleSavePortfolio}
                    disabled={saving}
                    className="pb-action-btn pb-save-btn"
                  >
                    {saving ? tt('Saving...') : tt('Save')}
                  </button>
                  <button
                    type="button"
                    onClick={openInBuilder}
                    className="pb-action-btn secondary"
                  >
                    {tt('Edit in Builder')}
                  </button>
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className="pb-action-btn secondary"
                  >
                    {tt('Export CSV')}
                  </button>
                </div>
              </div>
              {saveMessage && (
                <div className={`pb-save-message ${/saved|enregistre/i.test(saveMessage) ? 'success' : 'error'}`}>
                  {saveMessage}
                </div>
              )}
              <PortfolioSummary
                holdingsCount={holdings.length}
                investmentAmount={investmentAmount}
                totalWeight={totalWeight}
                tt={tt}
              />

              <MetricsPanel metrics={displayMetrics} tt={tt} />

              <AlwaysVisibleSection
                title={tt('Snapshot charts')}
                subtitle={tt('Quick allocation overview')}
                className="pb-disclosure"
              >
                <ChartsPanel
                  sectorData={sectorDistribution}
                  assetData={assetTypeDistribution}
                  topHoldingsData={topHoldingsDistribution}
                  riskReturnData={riskReturnData}
                  performanceData={performanceSeries}
                  historicalPerformance={historicalPerformance}
                  isBackendFrontierActive={isBackendFrontierActive}
                  frontierFallbackReason={frontierFallbackReason}
                  showSector={true}
                  showAsset={false}
                  showTopHoldings={true}
                  showPerformance={false}
                  showRiskReturn={false}
                  className="pb-chart-grid-top"
                  onHoldingSliceClick={openTickerInResearch}
                />
              </AlwaysVisibleSection>
            </div>
          </div>
        )}
      </div>

      {portfolio && !loading && holdings.length > 0 && (
        <div className="results-section pb-results-section-bottom">
          <AlwaysVisibleSection
            title={tt('Performance visuals')}
            subtitle={tt('Charts for trends, allocations, and frontier context')}
            className="pb-disclosure"
          >
            <ChartsPanel
              sectorData={sectorDistribution}
              assetData={assetTypeDistribution}
              riskReturnData={riskReturnData}
              performanceData={performanceSeries}
              historicalPerformance={historicalPerformance}
              isBackendFrontierActive={isBackendFrontierActive}
              frontierFallbackReason={frontierFallbackReason}
              showSector={false}
              showAsset={false}
              showPerformance={true}
              showRiskReturn={true}
            />
          </AlwaysVisibleSection>

          {holdings.length >= 2 && (
            <AlwaysVisibleSection
              title={tt('Advanced analytics')}
              subtitle={tt('Stress, backtest, attribution, drift, and benchmark diagnostics')}
              className="pb-disclosure"
            >
              <AnalyticsPanel
                activeTab={analyticsTab}
                onTabChange={setAnalyticsTab}
                benchmarkData={benchmarkData}
                backtestData={backtestData}
                stressData={stressData}
                riskDecompData={riskDecompData}
                driftData={driftData}
                brinsonData={brinsonData}
                frontierData={getEfficientFrontierData()}
                correlation={getCorrelationData()}
                isBackendFrontierActive={isBackendFrontierActive}
                frontierFallbackReason={frontierFallbackReason}
                onRun={runAnalytics}
                loading={analyticsLoading}
                costBps={costBps}
                onCostBpsChange={setCostBps}
                analyticsPeriod={analyticsPeriod}
                onAnalyticsPeriodChange={setAnalyticsPeriod}
                analyticsBenchmarkTicker={analyticsBenchmarkTicker}
                onAnalyticsBenchmarkTickerChange={setAnalyticsBenchmarkTicker}
                analyticsRebalanceFrequency={analyticsRebalanceFrequency}
                onAnalyticsRebalanceFrequencyChange={setAnalyticsRebalanceFrequency}
                driftThresholdPct={driftThresholdPct}
                onDriftThresholdPctChange={setDriftThresholdPct}
              />
            </AlwaysVisibleSection>
          )}

          <AlwaysVisibleSection
            title={`${tt('Holdings table')} (${holdings.length})`}
            subtitle={tt('Detailed holdings and allocation weights')}
            className="pb-disclosure"
          >
            <div className="holdings-table-wrapper">
              <div className="holdings-table pb-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{tt('Ticker')}</th>
                      <th>{tt('Market')}</th>
                      <th>{tt('Shares')}</th>
                      <th>{tt('Value')}</th>
                      <th>{tt('Weight')}</th>
                      <th>{tt('Sector')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {holdings.map((holding, idx) => (
                      <tr key={`${holding.ticker}-${idx}`}>
                        <td>
                          <button
                            type="button"
                            className="pb-ticker-link"
                            onClick={() => openTickerInResearch(holding.ticker)}
                            aria-label={`${tt('Open asset research for')} ${holding.ticker}`}
                          >
                            {holding.ticker}
                          </button>
                        </td>
                        <td>{holding.exchange || tt('N/A')}</td>
                        <td>{Number(holding.shares || 0).toFixed(2)}</td>
                        <td>${Number(holding.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                        <td>{Number(holding.weight || 0).toFixed(2)}%</td>
                        <td>{holding.sector || tt('N/A')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AlwaysVisibleSection>
        </div>
      )}
    </div>
  );
};

export default PortfolioGenerator;
